import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import * as actions from "../../features/ai-prompt-builder/redux/actions";
import * as collectionActions from "../../features/collections/redux/actions";
import * as pluginActions from "../../features/plugin/redux/actions";

import ParentContentCard from "../common/ParentContentCard";
import CommonPageComponent from "../../common/ui/CommonPageComponent";
import CustomLoader from "../common/CustomLoader";

import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";

import { PromptDataDisplay } from "../collections/PromptDataDisplay";
import { getFilteredCollections, iconStyle } from "./utils";

export class PromptCollectionsList extends Component {
  static propTypes = {
    aiPromptBuilder: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    collections: PropTypes.object.isRequired,
  };

  state = {
    createdCollections: [],
    removedCollectionTitles: [],
    collectionAndCreateAppPending: false,
    systemRoles: [],
    oldNameMap: [], // [{oldName: "Old", newName: "New"}]
  };

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;

    try {
      const { collections, projectId, version, plugin } = this.props;

      const coll = await collections.loadProjectCollections({ projectId, version });
      const systemRoles = await plugin.loadRoles({ projectId, version });

      if (this._isMounted) {
        this.setState({
          systemRoles: systemRoles && systemRoles.data ? systemRoles.data : [],
          createdCollections: coll && coll.data ? coll.data : [],
        });
      }
    } catch (err) {
      console.error("Error loading collections/roles:", err);
    }

    window.onbeforeunload = () => {
      const message = "Reloading will cause unsaved data loss. Continue?";
      return (event.returnValue = message);
    };
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.onbeforeunload = null;
  }

  /* ---------------------
   *  Utility: modify names
   *  - trim, lowercase, spaces -> underscore, remove invalid chars
   * --------------------*/
  modifyName = (name = "") =>
    name
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  //ADD OLD -> NEW NAME MAPPING
  handleAddNameMapping = (oldName, newName) => {
    if (!oldName) return;
    this.setState((prev) => {
      const existsIndex = prev.oldNameMap.findIndex((m) => m.oldName === oldName);

      // clone array
      const next = [...prev.oldNameMap];

      if (existsIndex >= 0) {
        next[existsIndex] = { oldName, newName };
      } else {
        next.push({ oldName, newName });
      }

      return { oldNameMap: next };
    });
  };

  /* ---------------------
   *  applyNameMapping to all refrences
   *  - recursive: handles nested arrays
   *  - compares oldName -> current ref
   *  - replaces refCollection.collectionName with newName
   * --------------------*/
  applyNameMapping = (fields = []) => {
    const { oldNameMap } = this.state;
    if (!Array.isArray(fields)) return fields;

    // build sanitized lookup: key = sanitized oldName, value = raw newName
    const lookup = (oldNameMap || []).reduce((acc, m) => {
      if (m && m.oldName) {
        acc[this.modifyName(m.oldName)] = m.newName || "";
      }
      return acc;
    }, {});

    const wrapper = (arr) =>
      arr.map((fld) => {
        // nested array of fields
        if (Array.isArray(fld)) {
          return wrapper(fld);
        }

        // non-object items
        if (!fld || typeof fld !== "object") {
          return fld;
        }

        // Rare case: field may contain nested 'fields' property (object)
        if (fld.fields && Array.isArray(fld.fields)) {
          return {
            ...fld,
            fields: wrapper(fld.fields),
          };
        }

        // Process reference fields
        if (
          fld.type === "reference" &&
          fld.refCollection &&
          fld.refCollection.collectionName
        ) {
          const currentRef = this.modifyName(fld.refCollection.collectionName);
          const mappedNewRaw = lookup[currentRef];

          if (mappedNewRaw) {
            const cleanName = this.modifyName(mappedNewRaw);
            return {
              ...fld,
              refCollection: {
                ...fld.refCollection,
                collectionName: cleanName,
              },
            };
          }
        }

        return fld;
      });

    return wrapper(fields);
  };

  
  handleFinalPromptSubmit = async (jsonConfig) => {
    const { collections, projectId, version } = this.props;

    try {
      const data = {
        ...jsonConfig,
        projectId,
        version,
        shouldUpdateIfExists: true,
        isCreateEvents: true,
      };

      // deep clone and map references (avoids mutation)
      const updatedFields = this.applyNameMapping(
        JSON.parse(JSON.stringify(data.fields || []))
      );

      const finalData = { ...data, fields: updatedFields };

      await collections.createCollectionWithPrompt(finalData);

      const allCollections = await collections.loadProjectCollections({ projectId, version });
      if (this._isMounted) {
        this.setState({
          createdCollections: allCollections && allCollections.data ? allCollections.data : [],
        });
      }
    } catch (err) {
      console.error("handleFinalPromptSubmit error:", err);
      throw err;
    }
  };

  // NEW: Track when collection name is updated
  handleCollectionNameUpdate = (oldName, newName) => {
    this.setState((prevState) => ({
      collectionNameUpdates: {
        ...prevState.collectionNameUpdates,
        [oldName]: newName,
      },
    }));
  };

  /* ---------------------
   *  handleRemoveCollection - remove already generated collection
   * --------------------*/
  handleRemoveCollection = (collectionTitle) => {
    if (!collectionTitle) return;
    this.setState((prev) => ({
      removedCollectionTitles: [...prev.removedCollectionTitles, collectionTitle],
    }));
  };

  
  modifyId = (str) =>
    !str ? "" : str.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-_]/g, "");

  
  renderCollection = (collection) => {
    const { REACT_APP_AI_MODEL } = process.env;
    const fields = collection && collection.fields ? collection.fields : [];
    const collectionTitle = collection && collection.collectionTitle ? collection.collectionTitle : "";
    const uuid = collection && collection.uuid ? collection.uuid : null;

    const isSystemCollection = collectionTitle === "User" || collectionTitle === "Role";
    const isSaved = Boolean(uuid);

    const formattedFieldTitles = Array.isArray(fields)
      ? fields.map((f) => ({
          title: f && f.fieldTitle && f.fieldTitle.en ? f.fieldTitle.en : "Untitled",
          type: f && f.type ? f.type : "text",
          fieldName: f && f.fieldName ? f.fieldName : "",
        }))
      : [];

    const accordionId = this.modifyId(collectionTitle);

    return (
      <AccordionItem uuid={accordionId} key={uuid || accordionId}>
        <AccordionItemHeading>
          <AccordionItemButton style={{ position: "relative" }}>
            <strong>{collectionTitle}</strong>

            {!isSystemCollection && !isSaved ? (
              <button
                type="button"
                className="btn btn-sm btn-link text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  this.handleRemoveCollection(collectionTitle);
                }}
                style={{ ...iconStyle, padding: 0 }}
              >
                <i className="wb webconnect-trash-regular"></i>
              </button>
            ) : (
              <i
                className="wb webconnect-check-circle"
                style={{ ...iconStyle, color: "#93a828" }}
              ></i>
            )}
          </AccordionItemButton>
        </AccordionItemHeading>

        <AccordionItemPanel>
          <PromptDataDisplay
            extractedFields={[fields]}
            promptFields={formattedFieldTitles}
            promptName={collectionTitle}
            selectedAIModel={REACT_APP_AI_MODEL || "OPEN_AI"}
            onUpdateJsonConfig={(config) => {
              const newName = config && config.collectionTitle ? config.collectionTitle : "";
              if (newName && newName !== collectionTitle) {
                this.handleAddNameMapping(collectionTitle, newName);
              }
            }}
            onSubmit={this.handleFinalPromptSubmit}
            showCancel={false}
            isSavedCollection={isSaved}
          />
        </AccordionItemPanel>
      </AccordionItem>
    );
  };

  /* ---------------------
   *  handleSaveAndContinue
   *  modification before bulk create
   * --------------------*/
  handleSaveAndContinue = async () => {
    const {
      collections,
      projectId,
      version,
      promptCollections = [],
      actions,
      history,
      rolesList,
      userPrompt,
    } = this.props;

    const { removedCollectionTitles, oldNameMap, systemRoles } = this.state;

    try {
      if (this._isMounted) {
        this.setState({ collectionAndCreateAppPending: true });
      }

      // Fetch stored collections
      const stored = await collections.loadProjectCollections({ projectId, version });
      const storedData = stored && stored.data ? stored.data : [];

      const storedNames = storedData.map((c) =>
        c && c.orgCollectionName ? (c.orgCollectionName + "").toLowerCase() : ""
      );

      // modified lookup arrays
      const renamedOld = (oldNameMap || []).map((m) => this.modifyName(m.oldName || ""));
      const renamedNew = (oldNameMap || []).map((m) => this.modifyName(m.newName || ""));

      // NEW collections to save
      const newCollections = (promptCollections || []).filter((c) => {
        const name = c && c.collectionTitle ? (c.collectionTitle + "").toLowerCase() : "";
        return (
          name &&
          !storedNames.includes(name) &&
          !removedCollectionTitles.includes(c.collectionTitle) &&
          !renamedOld.includes(name)
        );
      });

      // Renamed collections (current titles match a sanitized renamedNew)
      const renamedCollections = (promptCollections || []).filter((c) => {
        const title = c && c.collectionTitle ? this.modifyName(c.collectionTitle) : "";
        return renamedNew.includes(title);
      });

      let finalList = [...newCollections, ...renamedCollections];

      // Apply modification and name mapping to every collection before saving
      finalList = finalList.map((collection) => {
        const rawTitle = collection && collection.collectionTitle ? collection.collectionTitle : "";
        const sanitizedTitle = this.modifyName(rawTitle);

        const updatedFields = this.applyNameMapping(
          JSON.parse(JSON.stringify(collection && collection.fields ? collection.fields : []))
        );

        return {
          ...collection,
          collectionTitle: sanitizedTitle,
          fields: updatedFields,
        };
      });

      if (finalList.length > 0) {
        await actions.createBulkCollections({
          dataForSave: finalList,
          projectId,
          version,
          isCreateEvents: true,
        });
      }

      // navigate
      history.push({
        pathname: `/2.0/ai-prompt-builder/${version}/v/${projectId}/roles-list`,
        state: {
          rolesData: rolesList,
          systemRoles: JSON.parse(JSON.stringify(systemRoles || [])),
          userPrompt,
        },
      });
    } catch (error) {
      console.error("Save & Continue Error:", error);
    } finally {
      if (this._isMounted) {
        this.setState({ collectionAndCreateAppPending: false });
      }
    }
  };


  render() {
    const { promptCollections = [] } = this.props;
    const { createdCollections, removedCollectionTitles, collectionAndCreateAppPending } = this.state;

    const filteredCollections = (getFilteredCollections(promptCollections, createdCollections) || []).filter(
      (c) => !(removedCollectionTitles || []).includes(c && c.collectionTitle ? c.collectionTitle : "")
    );

    const preExpand = filteredCollections.length > 0 ? filteredCollections[0].collectionTitle : "";

    return (
      <CommonPageComponent>
        <div className="row mx-auto">
          <div className="col-md-12">
            <ParentContentCard title="Generated Collections List" icon="aiModelConfig">
              <div className="center-div mb-4">
                <h5 className="text-center">
                  The following collections were generated based on your prompt. You may modify them below.
                </h5>
              </div>

              {filteredCollections.length > 0 ? (
                <Accordion allowZeroExpanded preExpanded={[preExpand]}>
                  {filteredCollections.map(this.renderCollection)}
                </Accordion>
              ) : (
                <h4 className="text-center mt-4">No Collections Created</h4>
              )}
            </ParentContentCard>

            <div className="text-right mt-2">
              <button
                className="btn btn-theme-submit btn-md"
                onClick={this.handleSaveAndContinue}
                disabled={collectionAndCreateAppPending}
              >
                <i className="wb webconnect-save-regular"></i>&nbsp; Create Collections & Continue
                <CustomLoader loading={collectionAndCreateAppPending} />
              </button>
            </div>
          </div>
        </div>
      </CommonPageComponent>
    );
  }
}


const mapStateToProps = (state) => ({
  aiPromptBuilder: state.aiPromptBuilder,
  collections: state.collections,
  home: state.home,
  plugin: state.plugin,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({ ...actions }, dispatch),
  collections: bindActionCreators({ ...collectionActions }, dispatch),
  plugin: bindActionCreators({ ...pluginActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PromptCollectionsList);